import { App, DatePicker, Select, Spin } from 'antd';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { REPORT_STATUS, REPORT_STATUS_LABELS } from '@/constants/report';
import { ReportHistoryDetailDrawer } from '@/pages/employee/history/ReportHistoryDetailDrawer';
import { ApiError } from '@/services/api/client';
import * as reportsApi from '@/services/api/reports';
import { useAuthStore } from '@/stores/auth.store';
import type { ReportRecord, ReportStatus } from '@/types/report';
import { getTodayStatusMeta, type EmployeeTodayStatus } from '@/utils/employee-dashboard';

interface AppliedFilters {
  startDate?: string;
  endDate?: string;
  status?: ReportStatus;
}

const HISTORY_STATUS_OPTIONS = [
  { value: REPORT_STATUS.SUBMITTED, label: REPORT_STATUS_LABELS.SUBMITTED },
  { value: REPORT_STATUS.REVIEWED, label: REPORT_STATUS_LABELS.REVIEWED },
  { value: REPORT_STATUS.COMMENTED, label: REPORT_STATUS_LABELS.COMMENTED },
];

function historyStatusClass(status: ReportStatus): string {
  const meta = getTodayStatusMeta(status as EmployeeTodayStatus);
  return meta.colorClass;
}

export function ReportHistoryMobilePage() {
  const { message } = App.useApp();
  const [searchParams] = useSearchParams();
  const currentUser = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [filters, setFilters] = useState<AppliedFilters>({});
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [status, setStatus] = useState<ReportStatus | undefined>();

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    setLoading(true);
    try {
      const result = await reportsApi.fetchReports({
        page: 1,
        pageSize: 50,
        userId: currentUser.id,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status,
      });

      const ownItems = result.items.filter(
        (item) => item.userId === currentUser.id && item.status !== REPORT_STATUS.DRAFT,
      );
      setReports(ownItems);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载历史日报失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [currentUser, filters, message]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  useEffect(() => {
    const reportId = searchParams.get('reportId');
    if (reportId) {
      setDetailReportId(reportId);
      setDetailOpen(true);
    }
  }, [searchParams]);

  const applyFilters = () => {
    const next: AppliedFilters = { status };
    if (dateRange?.[0]) {
      next.startDate = dateRange[0].format('YYYY-MM-DD');
    }
    if (dateRange?.[1]) {
      next.endDate = dateRange[1].format('YYYY-MM-DD');
    }
    setFilters(next);
  };

  const openDetail = (record: ReportRecord) => {
    setDetailReportId(record.id);
    setDetailOpen(true);
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div>
      <div className="mobile-filter-panel">
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">日期</span>
          <DatePicker.RangePicker
            allowClear
            value={dateRange}
            onChange={(value) => setDateRange(value)}
            style={{ flex: 1 }}
          />
        </div>
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">状态</span>
          <Select
            allowClear
            placeholder="全部"
            style={{ flex: 1 }}
            value={status}
            onChange={(value) => setStatus(value)}
            options={HISTORY_STATUS_OPTIONS}
          />
        </div>
        <button type="button" className="mobile-btn mobile-btn--primary mobile-btn--block" onClick={applyFilters}>
          查询
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : reports.length === 0 ? (
        <MobileEmpty message="暂无历史日报" />
      ) : (
        reports.map((report) => (
          <article
            key={report.id}
            className="mobile-card"
            role="button"
            tabIndex={0}
            onClick={() => openDetail(report)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                openDetail(report);
              }
            }}
          >
            <div className="mobile-card__header">
              <span className="mobile-card__title">
                {new Date(report.reportDate).toLocaleDateString('zh-CN')}
              </span>
              <span className={`mobile-status-badge ${historyStatusClass(report.status)}`}>
                {REPORT_STATUS_LABELS[report.status]}
              </span>
            </div>
            <div className="mobile-card__meta">
              提交时间：
              {report.submittedAt
                ? new Date(report.submittedAt).toLocaleString('zh-CN')
                : new Date(report.createdAt).toLocaleString('zh-CN')}
            </div>
          </article>
        ))
      )}

      <ReportHistoryDetailDrawer
        open={detailOpen}
        reportId={detailReportId}
        width="100%"
        placement="bottom"
        height="88vh"
        isMobile
        onClose={() => {
          setDetailOpen(false);
          setDetailReportId(null);
        }}
        onReadStatusChanged={() => void loadReports()}
      />
    </div>
  );
}
